import { Api } from "../constants";
import { buildEndpointUrl } from "./http-client-url-builder";
import { enqueueRefreshToken } from "./refresh-token-queue";
import type {
  SseEndpointsWithMethod,
  SseFetchOptions,
  SseHttpClientFetchOptions,
  SseHttpClientOptions,
  SseOpenApiOperation,
  SseOpenApiPaths,
} from "./sse-http-client.types";

export interface SseChunkPayload {
  data?: string;
  event?: string;
  id?: string;
}

export class SseHttpClient<P extends SseOpenApiPaths> {
  constructor(
    readonly baseUrl: string,
    readonly options: SseHttpClientOptions,
  ) {}

  async postStream<E extends SseEndpointsWithMethod<P>, O extends SseOpenApiOperation = NonNullable<P[E]["post"]>>(
    endpoint: E,
    options: SseHttpClientFetchOptions<O>,
    onChunk: (chunk: SseChunkPayload) => void,
    onError?: (error: unknown) => void,
  ): Promise<Response> {
    const { path = {}, query = {}, fetchOptions = {} } = options;
    const url = buildEndpointUrl(this.baseUrl, endpoint, path, query);
    const requestInit = this.createRequestInit(fetchOptions);

    return await this.executeRequest(url, requestInit, onChunk, onError);
  }

  private createRequestInit(fetchOptions: SseFetchOptions): RequestInit {
    return {
      method: "POST",
      body: fetchOptions.body ? JSON.stringify(fetchOptions.body) : undefined,
      headers: {
        ...(this.options?.tenantId && {
          "X-Tenant-Id": this.options?.tenantId,
        }),
        "Content-Type": "application/json",
        ...this.parseHeaders(fetchOptions.headers),
      },
      credentials: "include",
      signal: fetchOptions.signal,
    };
  }

  private async executeRequest(
    url: string,
    requestInit: RequestInit,
    onChunk: (chunk: SseChunkPayload) => void,
    onError?: (error: unknown) => void,
  ): Promise<Response> {
    const response = await fetch(url, requestInit);

    if (response.status === 403) {
      const refreshResponse = await this.requestRefreshToken();

      if (refreshResponse.ok) {
        const retryResponse = await fetch(url, requestInit);
        if (!retryResponse.ok) {
          this.options?.onRetryFail?.(retryResponse);
          return retryResponse;
        }
        return this.handleStreamResponse(retryResponse, onChunk, requestInit.signal, onError);
      }

      this.options?.onRetryFail?.(refreshResponse);
      return response;
    }

    return this.handleStreamResponse(response, onChunk, requestInit.signal, onError);
  }

  private async handleStreamResponse(
    response: Response,
    onChunk: (chunk: SseChunkPayload) => void,
    signal?: AbortSignal | null,
    onError?: (error: unknown) => void,
  ): Promise<Response> {
    if (!response.ok) {
      return response;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("No reader available");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    (async () => {
      try {
        while (true) {
          if (signal?.aborted) {
            await reader.cancel();
            break;
          }

          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const events = buffer.split("\n\n");
          buffer = events.pop() || "";

          for (const event of events) {
            if (!event.trim()) continue;
            this.parseAndDispatchEvent(event, onChunk);
          }
        }

        if (buffer.trim()) {
          this.parseAndDispatchEvent(buffer, onChunk);
        }

        decoder.decode();
      } catch (e) {
        if (signal?.aborted) return;
        console.error("Error in stream reading:", e);
        onError?.(e);
      }
    })();

    return response;
  }

  private parseAndDispatchEvent(event: string, onChunk: (chunk: SseChunkPayload) => void): void {
    const lines = event.split("\n");
    const chunkPayload: SseChunkPayload = {};

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      const [prefix, ...rest] = trimmed.split(":");
      const value = rest.join(":").trim();

      switch (prefix) {
        case "data":
          chunkPayload.data = value;
          break;
        case "event":
          chunkPayload.event = value;
          break;
        case "id":
          chunkPayload.id = value;
          break;
      }
    }

    const isChunkNotEmpty = Object.keys(chunkPayload).length > 0;
    isChunkNotEmpty && onChunk(chunkPayload);
  }

  private async requestRefreshToken(): Promise<Response> {
    return await enqueueRefreshToken(() =>
      fetch(buildEndpointUrl(Api.Account, "/refresh", {}, {}), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      }),
    );
  }

  private parseHeaders(headers?: HeadersInit) {
    const entries = headers instanceof Headers ? headers.entries() : new Headers(headers).entries();
    return Object.fromEntries(Array.from(entries));
  }
}
